import { useState, useEffect, useRef } from 'react';
import { Dices, Trash2 } from 'lucide-react';
import { useAuth } from '../AuthModule';
import { ThemeToggle, useTheme } from '../ThemeModule';
import { HOROSCOPE_TEEMAPAKETTI_PRODUCT_ID } from './avatarDesignerZodiac';
import {
  API,
  AVATAR_IKONI_PRODUCT_ID,
  DEFAULT_BUBBLE_COLOR_HEX,
  PROFILE_FIELDS,
  defaultProfile,
  normalizeHexTo6,
  toColorInputValue,
} from './profileHelpers';
import ProfileNavTopBar from '../ProfileNavTopBar';
import AvatarDesignerModal from './AvatarDesignerModal';
import AvatarWithDesignerBadge from './AvatarWithDesignerBadge';
import { Randomaattori } from '../Randomaattori';
import { PULINA_CREDIT_PURCHASES_CHANGED } from '../../useStatisticsUnlock';
import { useI18n } from '../../i18n/I18nModule';
import { loadingPageText } from '../../i18n/loadingPage';
import { formatBotSenderDisplayName } from '../../i18n/botSender';
import { UI_LANGUAGES, DEFAULT_UI_LOCALE, isUiLocale } from '../../i18n/uiLanguages';

const SUCCESS_MESSAGE_KEY = 'settings.successSave';
const NOTIFICATION_TTL_MS = 4000;
const SAVE_BUTTON_FLASH_MS = 900;
const SETTINGS_TITLE_USERNAME_MAX = 20;

function truncateTitleUsername(value) {
  const raw = String(value || '').trim();
  if (raw.length <= SETTINGS_TITLE_USERNAME_MAX) return raw;
  return `${raw.slice(0, SETTINGS_TITLE_USERNAME_MAX)}...`;
}

export default function UserSettingsPage({ user }) {
  const { updateDisplayName, logout } = useAuth();
  const { theme } = useTheme();
  const { t, setLocale } = useI18n();
  const [profile, setProfile] = useState(null);
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(defaultProfile());
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [avatarUrlInput, setAvatarUrlInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const [notificationTone, setNotificationTone] = useState('success');
  const [bubbleColorInput, setBubbleColorInput] = useState(DEFAULT_BUBBLE_COLOR_HEX);
  const [newsletterChecked, setNewsletterChecked] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);
  const [descriptionHeaderInput, setDescriptionHeaderInput] = useState('');
  const [descriptionBodyInput, setDescriptionBodyInput] = useState('');
  const [saveButtonFlash, setSaveButtonFlash] = useState(false);
  const saveButtonFlashTimeoutRef = useRef(null);
  const saveButtonFlashRafRef = useRef(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxError, setInboxError] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [hasAvatarIkoniPurchase, setHasAvatarIkoniPurchase] = useState(false);
  const [hasHoroscopeTeemapaketti, setHasHoroscopeTeemapaketti] = useState(false);
  const [avatarDesignerOpen, setAvatarDesignerOpen] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [newPasswordConfirmInput, setNewPasswordConfirmInput] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [uiLocaleInput, setUiLocaleInput] = useState(DEFAULT_UI_LOCALE);
  const [localeSaving, setLocaleSaving] = useState(false);

  const showNotification = (message, tone = 'success') => {
    setNotificationTone(tone === 'error' ? 'error' : 'success');
    setNotification(message);
  };

  const userId = user?.id;
  const isMember = !!user?.role && user.role !== 'user';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), NOTIFICATION_TTL_MS);
    return () => clearTimeout(t);
  }, [notification]);

  useEffect(
    () => () => {
      if (saveButtonFlashTimeoutRef.current != null) {
        clearTimeout(saveButtonFlashTimeoutRef.current);
      }
      if (saveButtonFlashRafRef.current != null) {
        cancelAnimationFrame(saveButtonFlashRafRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/users/${encodeURIComponent(userId)}/profile`).then((res) => res.json()),
      fetch(`${API}/api/users/${encodeURIComponent(userId)}/settings`).then((res) => res.json()),
      fetch(
        `${API}/api/users/${encodeURIComponent(userId)}/credit-purchases/${encodeURIComponent(AVATAR_IKONI_PRODUCT_ID)}`
      ).then((res) => (res.ok ? res.json() : { quantity: 0 })),
      fetch(
        `${API}/api/users/${encodeURIComponent(userId)}/credit-purchases/${encodeURIComponent(HOROSCOPE_TEEMAPAKETTI_PRODUCT_ID)}`
      ).then((res) => (res.ok ? res.json() : { quantity: 0 })),
    ])
      .then(([profileData, settingsData, avatarIkoniPurchase, horoskoopitPurchase]) => {
        const p = profileData && typeof profileData === 'object' ? profileData : null;
        setProfile(p);
        const base = p ? { ...defaultProfile(), ...p, avatar: p.showAvatar ?? p.avatar ?? 'NO' } : defaultProfile();
        setForm({
          avatar: base.avatar || 'NO',
          allowDms: base.allowDms || 'NO',
          showAllowDms: base.showAllowDms ?? 'NO',
          contactEmail: base.contactEmail ?? '',
          website: base.website ?? '',
        });

        const settings = settingsData && typeof settingsData === 'object' ? settingsData : null;
        setUserSettings(settings);
        const aq =
          avatarIkoniPurchase && typeof avatarIkoniPurchase === 'object'
            ? Number(avatarIkoniPurchase.quantity) || 0
            : 0;
        setHasAvatarIkoniPurchase(aq > 0);
        const hq =
          horoskoopitPurchase && typeof horoskoopitPurchase === 'object'
            ? Number(horoskoopitPurchase.quantity) || 0
            : 0;
        setHasHoroscopeTeemapaketti(hq > 0);
        const hex = normalizeHexTo6(settings?.bubbleColor);
        setBubbleColorInput(hex || DEFAULT_BUBBLE_COLOR_HEX);
        setNewsletterChecked(settings?.newsletter === 'YES');
        setAvatarUrlInput(settings?.avatar && typeof settings.avatar === 'string' ? settings.avatar : '');
        setAvatarImageError(false);
        setDescriptionHeaderInput(settings?.descriptionHeader ?? '');
        setDescriptionBodyInput(settings?.descriptionBody ?? '');
        const loadedLocale = settings?.uiLocale ?? settings?.ui_locale;
        if (isUiLocale(loadedLocale)) {
          setUiLocaleInput(loadedLocale);
          setLocale(loadedLocale);
        }
        if (user) setDisplayNameInput(settings?.displayName ?? user?.displayName ?? user?.username ?? '');
      })
      .catch(() => {
        setProfile(null);
        setUserSettings(null);
        setHasAvatarIkoniPurchase(false);
        setHasHoroscopeTeemapaketti(false);
        setBubbleColorInput(DEFAULT_BUBBLE_COLOR_HEX);
        setNewsletterChecked(false);
      })
      .finally(() => setLoading(false));
  }, [userId, user, setLocale]);

  useEffect(() => {
    if (!userId) return undefined;
    const refetchCreditUnlocks = () => {
      const uid = String(userId).replace(/n$/, '');
      Promise.all([
        fetch(
          `${API}/api/users/${encodeURIComponent(uid)}/credit-purchases/${encodeURIComponent(AVATAR_IKONI_PRODUCT_ID)}`
        ).then((res) => (res.ok ? res.json() : { quantity: 0 })),
        fetch(
          `${API}/api/users/${encodeURIComponent(uid)}/credit-purchases/${encodeURIComponent(HOROSCOPE_TEEMAPAKETTI_PRODUCT_ID)}`
        ).then((res) => (res.ok ? res.json() : { quantity: 0 })),
      ])
        .then(([ikoni, horos]) => {
          setHasAvatarIkoniPurchase((Number(ikoni.quantity) || 0) > 0);
          setHasHoroscopeTeemapaketti((Number(horos.quantity) || 0) > 0);
        })
        .catch(() => {});
    };
    const onPurchasesChanged = () => refetchCreditUnlocks();
    if (typeof window !== 'undefined') {
      window.addEventListener(PULINA_CREDIT_PURCHASES_CHANGED, onPurchasesChanged);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(PULINA_CREDIT_PURCHASES_CHANGED, onPurchasesChanged);
      }
    };
  }, [userId]);

  useEffect(() => {
    if (!inboxOpen || !userId) return;
    setInboxLoading(true);
    setInboxError(null);
    const userIdStr = String(userId).replace(/n$/, '');
    fetch(`${API}/api/direct-messages/inbox/${encodeURIComponent(userIdStr)}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : Array.isArray(data?.messages) ? data.messages : [];
        setInboxMessages(list);
      })
      .catch(() => setInboxError('Viestien lataus epäonnistui.'))
      .finally(() => setInboxLoading(false));
  }, [inboxOpen, userId]);

  const handleUiLocaleChange = (e) => {
    const code = e.target.value;
    if (!isUiLocale(code) || code === uiLocaleInput) return;

    const previous = uiLocaleInput;
    setUiLocaleInput(code);
    setLocaleSaving(true);

    const applyLocaleAndReload = (localeCode) => {
      setLocale(localeCode);
      if (typeof window !== 'undefined') window.location.reload();
    };

    if (!userId) {
      applyLocaleAndReload(code);
      return;
    }

    fetch(`${API}/api/users/${encodeURIComponent(userId)}/settings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uiLocale: code }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('save failed');
        const saved = await res.json();
        const savedLocale = saved?.uiLocale ?? saved?.ui_locale;
        if (!isUiLocale(savedLocale)) throw new Error('invalid locale in response');
        setUserSettings((prev) => (prev ? { ...prev, uiLocale: savedLocale } : { uiLocale: savedLocale }));
        applyLocaleAndReload(savedLocale);
      })
      .catch(() => {
        setUiLocaleInput(previous);
        showNotification(t('settings.errorSave'), 'error');
        setLocaleSaving(false);
      });
  };

  const handleCancel = () => {
    const hex = normalizeHexTo6(userSettings?.bubbleColor);
    setBubbleColorInput(hex || DEFAULT_BUBBLE_COLOR_HEX);
    setNewsletterChecked(userSettings?.newsletter === 'YES');
    setAvatarUrlInput(userSettings?.avatar && typeof userSettings.avatar === 'string' ? userSettings.avatar : '');
    setAvatarImageError(false);
    const base = profile ? { ...defaultProfile(), ...profile, avatar: profile.showAvatar ?? profile.avatar ?? 'NO' } : defaultProfile();
    setForm({
      avatar: base.avatar || 'NO',
      allowDms: base.allowDms || 'NO',
      contactEmail: base.contactEmail ?? '',
      website: base.website ?? '',
    });
    setDescriptionHeaderInput(userSettings?.descriptionHeader ?? '');
    setDescriptionBodyInput(userSettings?.descriptionBody ?? '');
    const resetLocale = userSettings?.uiLocale ?? userSettings?.ui_locale;
    if (isUiLocale(resetLocale)) {
      setUiLocaleInput(resetLocale);
      setLocale(resetLocale);
    }
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return;
    setSaving(true);
    const contactEmailVal = form.contactEmail && form.contactEmail.trim() ? form.contactEmail.trim() : null;
    const websiteVal = form.website && form.website.trim() ? form.website.trim() : null;
    const avatarVal = avatarUrlInput && avatarUrlInput.trim() ? avatarUrlInput.trim() : null;
    const bubbleHex = normalizeHexTo6(bubbleColorInput) || DEFAULT_BUBBLE_COLOR_HEX;
    const displayNameVal = displayNameInput && displayNameInput.trim() ? displayNameInput.trim() : (userSettings?.displayName ?? user?.displayName ?? null);
    const descriptionHeaderVal = descriptionHeaderInput && descriptionHeaderInput.trim() ? descriptionHeaderInput.trim() : null;
    const descriptionBodyVal = descriptionBodyInput && descriptionBodyInput.trim() ? descriptionBodyInput.trim() : null;
    const settingsPayload = {
      theme: theme === 'light' ? 'LIGHT' : 'DARK',
      uiLocale: uiLocaleInput,
      displayName: displayNameVal,
      contactEmail: contactEmailVal,
      website: websiteVal,
      avatar: avatarVal,
      bubbleColor: bubbleHex,
      newsletter: newsletterChecked ? 'YES' : 'NO',
      descriptionHeader: descriptionHeaderVal,
      descriptionBody: descriptionBodyVal,
    };
    const profilePayload = {
      showAvatar: form.avatar,
      allowDms: form.allowDms,
      showAllowDms: form.showAllowDms,
      contactEmail: contactEmailVal,
      website: websiteVal,
    };
    Promise.all([
      fetch(`${API}/api/users/${encodeURIComponent(userId)}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsPayload),
      }),
      fetch(`${API}/api/users/${encodeURIComponent(userId)}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profilePayload),
      }),
    ])
      .then(async ([settingsRes, profileRes]) => {
        if (!settingsRes.ok || !profileRes.ok) {
          // Prefer a more specific error message from settings endpoint (e.g. display name uniqueness).
          if (!settingsRes.ok) {
            if (settingsRes.status === 404) {
              showNotification(t('settings.errorSettingsNotFound'), 'error');
              return;
            }
            try {
              const err = await settingsRes.json();
              if (err && err.error === 'DISPLAY_NAME_TAKEN') {
                showNotification(t('settings.errorDisplayNameTaken'), 'error');
                return;
              }
            } catch {
              // fall through to generic error
            }
          }
          showNotification(t('settings.errorSave'), 'error');
          return;
        }
        const [settingsData, profileData] = await Promise.all([
          settingsRes.json(),
          profileRes.json(),
        ]);
        setUserSettings(settingsData);
        setBubbleColorInput(normalizeHexTo6(settingsData?.bubbleColor) || DEFAULT_BUBBLE_COLOR_HEX);
        setProfile(profileData);
        showNotification(t(SUCCESS_MESSAGE_KEY), 'success');
        if (settingsData?.displayName != null && user) {
          updateDisplayName(settingsData.displayName);
        }
        if (typeof window !== 'undefined' && userId != null) {
          window.dispatchEvent(new CustomEvent('user-settings-updated', { detail: { userId } }));
        }
        if (typeof window !== 'undefined') {
          window.location.href = '/profiili';
        }
        return Promise.all([
          fetch(`${API}/api/users/${encodeURIComponent(userId)}/profile`).then((res) => res.json()),
          fetch(`${API}/api/users/${encodeURIComponent(userId)}/settings`).then((res) => res.json()),
        ]).then(([pData, sData]) => {
          const p = pData && typeof pData === 'object' ? pData : null;
          setProfile(p);
          const base = p ? { ...defaultProfile(), ...p, avatar: p.showAvatar ?? p.avatar ?? 'NO' } : defaultProfile();
          setForm({
            avatar: base.avatar || 'NO',
            allowDms: base.allowDms || 'NO',
            showAllowDms: base.showAllowDms ?? 'NO',
            contactEmail: base.contactEmail ?? '',
            website: base.website ?? '',
          });

          const settings = sData && typeof sData === 'object' ? sData : null;
          setUserSettings(settings);
          setBubbleColorInput(normalizeHexTo6(settings?.bubbleColor) || DEFAULT_BUBBLE_COLOR_HEX);
          setNewsletterChecked(settings?.newsletter === 'YES');
          setAvatarUrlInput(settings?.avatar && typeof settings.avatar === 'string' ? settings.avatar : '');
          setDescriptionHeaderInput(settings?.descriptionHeader ?? '');
          setDescriptionBodyInput(settings?.descriptionBody ?? '');
          if (user) setDisplayNameInput(settings?.displayName ?? user?.displayName ?? user?.username ?? '');
        });
      })
      .catch(() => showNotification(t('settings.errorSave'), 'error'))
      .finally(() => setSaving(false));
  };

  const handleLogout = () => {
    logout();
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  const closeInbox = () => {
    setInboxOpen(false);
  };

  const handleClearInbox = () => {
    if (!window.confirm('Haluatko varmasti tyhjentää koko postilaatikon? Kaikki viestit poistetaan pysyvästi.')) return;
    if (!userId) return;
    const userIdStr = String(userId).replace(/n$/, '');
    fetch(`${API}/api/direct-messages/inbox/${encodeURIComponent(userIdStr)}`, { method: 'DELETE' })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(() => {
        setInboxMessages([]);
        setInboxError(null);
      })
      .catch(() => {
        setInboxError('Postilaatikon tyhjennys epäonnistui.');
      });
  };

  const handlePasswordChangeSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    console.log('[password-change][ui] submit clicked');
    if (!userId) return;
    const currentPassword = currentPasswordInput;
    const newPassword = newPasswordInput.trim();
    const newPasswordConfirm = newPasswordConfirmInput.trim();
    console.log('[password-change][ui] input snapshot', {
      userId: String(userId),
      hasCurrentPassword: Boolean(currentPassword),
      newPasswordLength: newPassword.length,
      confirmLength: newPasswordConfirm.length,
    });
    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      console.log('[password-change][ui] blocked: missing fields');
      setPasswordMessage('Täytä kaikki salasanakentät.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      console.log('[password-change][ui] blocked: new password mismatch');
      setPasswordMessage('Uusi salasana ja vahvistus eivät täsmää.');
      return;
    }
    setPasswordSaving(true);
    setPasswordMessage(null);
    console.log('[password-change][ui] sending API request');
    fetch(`${API}/api/users/${encodeURIComponent(userId)}/change-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: String(userId).replace(/n$/, ''),
        currentPassword,
        newPassword,
      }),
    })
      .then(async (res) => {
        console.log('[password-change][ui] response status', res.status);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.message || 'Salasanan vaihto epäonnistui.');
        return data;
      })
      .then((data) => {
        console.log('[password-change][ui] success', data);
        const msg = data.message || 'Salasana vaihdettu.';
        setPasswordMessage(msg);
        showNotification(msg, 'success');
        setCurrentPasswordInput('');
        setNewPasswordInput('');
        setNewPasswordConfirmInput('');
      })
      .catch((err) => {
        console.log('[password-change][ui] failed', err);
        const msg = err.message || 'Salasanan vaihto epäonnistui.';
        setPasswordMessage(msg);
        showNotification(msg, 'error');
      })
      .finally(() => {
        console.log('[password-change][ui] request finished');
        setPasswordSaving(false);
      });
  };

  return (
    <div className="App-block">
      <ProfileNavTopBar user={user} currentPage="settings" />
      {loading ? (
        <p>{loadingPageText(t, 'settings')}</p>
      ) : (
        <>
          <div className="Settings-view">
            <div className="Profile-header Profile-header--full-width">
              <h2 className="Profile-heading">
                {truncateTitleUsername(user.displayName || user.username)} :: {t('settings.titleSuffix')}
              </h2>
            </div>
            <div className="Settings-content">
              <div className="Settings-ui-language Profile-form-field">
                <label htmlFor="ui-locale-select" className="CreateEvent-field-required">
                  {t('settings.uiLanguage')}
                </label>
                <select
                  id="ui-locale-select"
                  value={uiLocaleInput}
                  onChange={handleUiLocaleChange}
                  className="Settings-locale-select CreateEvent-time-select"
                  required
                  disabled={localeSaving}
                  aria-label={t('settings.uiLanguage')}
                  aria-busy={localeSaving}
                >
                  {UI_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.nativeLabel}
                    </option>
                  ))}
                </select>
              </div>
              {notification && (
                <span
                  className={`Profile-notification Profile-notification--${notificationTone}`}
                  role="status"
                  aria-live="polite"
                >
                  {notification}
                </span>
              )}
              <div className="Settings-theme">
                <span>{t('settings.theme')}: &nbsp;</span>
                <ThemeToggle />
              </div>
              <form className="Settings-form Profile-form" onSubmit={handleProfileSubmit}>
            {!isAdmin ? (
              <div className="Profile-form-field">
                <label htmlFor="display-name-input">{t('settings.displayName')}</label>
                <div className="Profile-display-name-row">
                  <input
                    id="display-name-input"
                    type="text"
                    placeholder={user?.displayName ?? user?.username}
                    value={displayNameInput}
                    onChange={(e) => setDisplayNameInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="Profile-dice-button"
                    onClick={() => setDisplayNameInput(Randomaattori())}
                    aria-label={t('settings.randomDisplayName')}
                    title={t('settings.randomDisplayName')}
                  >
                    <Dices size={20} strokeWidth={2} />
                  </button>
                </div>
              </div>
            ) : null}
            {!isAdmin ? (
              <label className="Settings-newsletter-label">
                <input
                  type="checkbox"
                  checked={newsletterChecked}
                  onChange={(e) => setNewsletterChecked(e.target.checked)}
                />
                {t('settings.newsletter')}
              </label>
            ) : null}
            <div className="Profile-form-field">
              <label htmlFor="avatar-url">{t('settings.avatarUrl')}</label>
              <input
                id="avatar-url"
                type="url"
                value={avatarUrlInput}
                onChange={(e) => { setAvatarUrlInput(e.target.value); setAvatarImageError(false); }}
                placeholder="https://…"
                className="Profile-avatar-input"
              />
              <div className="Profile-avatar-preview-row">
                {avatarUrlInput.trim() ? (
                  <div className="Profile-avatar-wrap">
                    {!avatarImageError && (
                      <AvatarWithDesignerBadge designer={userSettings?.avatarDesigner} variant="settings">
                        <img
                          src={avatarUrlInput.trim()}
                          alt=""
                          className="Profile-avatar"
                          onError={() => setAvatarImageError(true)}
                        />
                      </AvatarWithDesignerBadge>
                    )}
                    {avatarImageError && (
                      <span className="Profile-avatar-error">Kuvaa ei voitu ladata</span>
                    )}
                  </div>
                ) : null}
                <div
                  className={`Profile-avatar-icon-picker${
                    hasAvatarIkoniPurchase ? '' : ' Profile-avatar-icon-picker--locked'
                  }`}
                >
                  <button
                    type="button"
                    className="Button AvatarDesigner-open-button"
                    disabled={!hasAvatarIkoniPurchase}
                    onClick={() => hasAvatarIkoniPurchase && setAvatarDesignerOpen(true)}
                  >
                    {t('settings.avatarIconSelector')}
                  </button>
                </div>
              </div>
              {!hasAvatarIkoniPurchase && (
                <p className="Profile-avatar-icon-picker-hint">
                  {isMember ? (
                    <>
                      Osta ensin tuote «OSTA AVATAR IKONI»{' '}
                      <a href="/kauppa">kaupasta</a>, jotta voit valita avatar-ikonit.
                    </>
                  ) : (
                    <>Jäsenenä voit ostaa «OSTA AVATAR IKONI» krediittikaupasta ja käyttää ikonivalitsinta.</>
                  )}
                </p>
              )}
            </div>
            <div className="Settings-password-form">
              <h3 className="Settings-password-title">{t('settings.changePassword')}</h3>
              <label>
                {t('settings.passwordCurrent')}
                <input
                  type="password"
                  value={currentPasswordInput}
                  onChange={(e) => setCurrentPasswordInput(e.target.value)}
                  autoComplete="current-password"
                />
              </label>
              <label>
                {t('settings.passwordNew')}
                <input
                  type="password"
                  value={newPasswordInput}
                  onChange={(e) => setNewPasswordInput(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              <label>
                {t('settings.passwordConfirm')}
                <input
                  type="password"
                  value={newPasswordConfirmInput}
                  onChange={(e) => setNewPasswordConfirmInput(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              {passwordMessage ? (
                <p className="Settings-password-message" role="status">
                  {passwordMessage}
                </p>
              ) : null}
              <button type="button" className="Button" disabled={passwordSaving} onClick={handlePasswordChangeSubmit}>
                {passwordSaving ? t('settings.passwordChanging') : t('settings.passwordChange')}
              </button>
            </div>
            <div className="Profile-form-field">
              <label htmlFor="description-header-input">{t('settings.descriptionHeader')}</label>
              <input
                id="description-header-input"
                type="text"
                value={descriptionHeaderInput}
                onChange={(e) => setDescriptionHeaderInput(e.target.value)}
                placeholder="Kuka olet lyhyesti"
              />
            </div>
            <div className="Profile-form-field">
              <label htmlFor="description-body-input">{t('settings.descriptionBody')}</label>
              <textarea
                id="description-body-input"
                rows={4}
                value={descriptionBodyInput}
                onChange={(e) => setDescriptionBodyInput(e.target.value)}
                placeholder="Kerro hieman itsestäsi..."
              />
            </div>
            <div className="Profile-form-field Profile-bubble-color-row">
              <div className="Profile-bubble-color-wrap">
                <div
                  className="Profile-bubble-color-swatch"
                  style={{ backgroundColor: toColorInputValue(bubbleColorInput) }}
                  aria-hidden
                />
                <input
                  id="bubble-color-picker"
                  type="color"
                  value={toColorInputValue(bubbleColorInput)}
                  onChange={(e) => {
                    const hexNoHash = normalizeHexTo6(e.target.value) || DEFAULT_BUBBLE_COLOR_HEX;
                    setBubbleColorInput(hexNoHash);
                    if (saveButtonFlashTimeoutRef.current != null) {
                      clearTimeout(saveButtonFlashTimeoutRef.current);
                      saveButtonFlashTimeoutRef.current = null;
                    }
                    if (saveButtonFlashRafRef.current != null) {
                      cancelAnimationFrame(saveButtonFlashRafRef.current);
                      saveButtonFlashRafRef.current = null;
                    }
                    setSaveButtonFlash(false);
                    saveButtonFlashRafRef.current = requestAnimationFrame(() => {
                      saveButtonFlashRafRef.current = null;
                      setSaveButtonFlash(true);
                      saveButtonFlashTimeoutRef.current = setTimeout(() => {
                        setSaveButtonFlash(false);
                        saveButtonFlashTimeoutRef.current = null;
                      }, SAVE_BUTTON_FLASH_MS);
                    });
                  }}
                  className="Profile-bubble-color-input"
                  aria-label="Viestikuplan väri"
                  title="Valitse väri"
                />
              </div>
              <label htmlFor="bubble-color-picker" className="Profile-bubble-color-label">
                {t('settings.bubbleColor')}
              </label>
            </div>
            <label className="Settings-checkbox-row Settings-newsletter-label">
              <input
                type="checkbox"
                checked={form.allowDms === 'YES'}
                onChange={(e) => setForm((f) => ({ ...f, allowDms: e.target.checked ? 'YES' : 'NO' }))}
              />
              {t('settings.privateMessages')}
            </label>

            {PROFILE_FIELDS.filter((f) => f.type !== 'image' && f.key !== 'allowDms').map((field) => (
              <div key={field.key} className="Profile-form-field">
                {field.type === 'yesno' ? (
                  <label className="Settings-checkbox-row">
                    <input
                      type="checkbox"
                      checked={form[field.key] === 'YES'}
                      onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    {field.label}
                  </label>
                ) : (
                  <label>
                    {field.key === 'contactEmail'
                      ? t('settings.contactEmail')
                      : field.key === 'website'
                        ? t('settings.website')
                        : field.suomennos}
                    <input
                      type="text"
                      value={form[field.key] ?? ''}
                      onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                      placeholder={
                        field.key === 'contactEmail' ? t('settings.contactEmailPlaceholder') : 'https://…'
                      }
                    />
                  </label>
                )}
              </div>
            ))}
            <div className="Settings-buttons">
              <button type="button" className="Button Settings-cancel-button" onClick={handleCancel}>
                {t('settings.cancel')}
              </button>
              <button
                type="submit"
                className={`Button Settings-save-button${saveButtonFlash ? ' Settings-save-button--flash' : ''}`}
                disabled={saving}
              >
                {saving ? t('settings.saving') : t('settings.save')}
              </button>
            </div>
              </form>
              <div className="Settings-logout-section">
                <button type="button" className="Button Settings-logout-button" onClick={handleLogout}>
                  {t('settings.logout')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <AvatarDesignerModal
        open={avatarDesignerOpen}
        onClose={() => setAvatarDesignerOpen(false)}
        userId={userId}
        initialDesigner={userSettings?.avatarDesigner}
        avatarUrl={avatarUrlInput.trim() || undefined}
        avatarImageError={avatarImageError}
        displayName={displayNameInput || user?.displayName}
        username={user?.username}
        bubbleColorHex={bubbleColorInput}
        hasHoroscopeTeemapaketti={hasHoroscopeTeemapaketti}
        onSaved={(d) => {
          setUserSettings((prev) => (prev && typeof prev === 'object' ? { ...prev, avatarDesigner: d } : prev));
          if (typeof window !== 'undefined' && userId != null) {
            window.dispatchEvent(new CustomEvent('user-settings-updated', { detail: { userId } }));
          }
        }}
      />

      {inboxOpen && (
        <div className="Inbox-overlay">
          <div className="Inbox-window">
            <div className="Inbox-header-with-trash">
              <button
                type="button"
                className="Inbox-trash-button"
                onClick={handleClearInbox}
                aria-label="Tyhjennä postilaatikko"
                title="Tyhjennä postilaatikko"
              >
                <Trash2 size={20} aria-hidden />
              </button>
              <button
                type="button"
                className="Inbox-close-button"
                onClick={closeInbox}
              >
                Sulje postilaatikko
              </button>
            </div>
            <h3 className="Inbox-title">{t('messages.inboxTitle')}</h3>
            {inboxLoading ? (
              <p>{loadingPageText(t, 'inbox')}</p>
            ) : inboxError ? (
              <p className="Inbox-error">{inboxError}</p>
            ) : inboxMessages.length === 0 ? (
              <p>Ei viestejä.</p>
            ) : (
              <div className="Inbox-table">
                <div className="Inbox-header-row">
                  <div className="Inbox-col-from">{t('messages.from')}</div>
                  <div className="Inbox-col-subject">{t('messages.subject')}</div>
                  <div className="Inbox-col-time">{t('messages.time')}</div>
                </div>
                <div className="Inbox-rows">
                  {inboxMessages.map((m) => {
                    const from = formatBotSenderDisplayName(
                      {
                        senderKind: m.senderKind,
                        fromUserId: m.fromUserId,
                        fromDisplayName: m.fromDisplayName,
                        fromUsername: m.fromUsername,
                      },
                      t,
                    );
                    const created =
                      m.createdAt != null
                        ? new Date(m.createdAt).toLocaleString('fi-FI')
                        : '';
                    const unread = m.messageRead === false;
                    const originalPostRaw =
                      m.originalPostId != null && String(m.originalPostId).trim() !== ''
                        ? String(m.originalPostId).replace(/n$/, '')
                        : m.threadRootId != null && String(m.threadRootId).trim() !== ''
                          ? String(m.threadRootId).replace(/n$/, '')
                          : '';
                    const threadMailId =
                      originalPostRaw && /^\d+$/.test(originalPostRaw)
                        ? originalPostRaw
                        : null;
                    const openMailId =
                      threadMailId ||
                      (m.id != null ? String(m.id).replace(/n$/, '') : '');
                    const subjectHrefId = threadMailId || openMailId;
                    let subject = '';
                    if (m.inboxSubject && String(m.inboxSubject).trim()) {
                      subject = String(m.inboxSubject).trim();
                    } else if (m.content && typeof m.content === 'string') {
                      const words = m.content.trim().split(/\s+/);
                      subject = words.slice(0, 3).join(' ');
                      if (words.length > 3) {
                        subject += '…';
                      }
                    }
                    return (
                      <div
                        key={m.id}
                        className={`Inbox-row${
                          unread ? ' Inbox-row--unread' : ''
                        }`}
                        onClick={() => {
                          if (openMailId) {
                            window.location.href = `/mail/${openMailId}`;
                          }
                        }}
                      >
                        <div className="Inbox-col-from">{from}</div>
                        <div className="Inbox-col-subject">
                          {subject && subjectHrefId ? (
                            <a
                              className="Inbox-subject-link"
                              href={`/mail/${subjectHrefId}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {subject}
                            </a>
                          ) : (
                            subject || '—'
                          )}
                        </div>
                        <div className="Inbox-col-time">{created}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
